import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

type TestItem = {
  id: number;
  title: string;
  topic_name?: string;
  course_name: string;
  question_count: number;
  pass_mark: number;
  total_marks: number;
  assigned_users: string;
  status: string;
};

type SubmissionItem = {
  id: number;
  test_id: number;
  user_id: number;
  test_title: string;
  course_name: string;
  score: number;
  passed: boolean;
  selected_answers_json?: string;
  submitted_at: string;
};

type CourseItem = {
  id: number;
  category: string;
  name: string;
  instructor?: string;
  department?: string;
  assigned_user?: string;
  status?: string;
  students?: number;
};

type AssignedUserItem = {
  id?: number;
  name?: string;
  username?: string;
  email?: string;
  role?: string;
  department?: string;
};

type ActiveTab = "tests" | "courses" | "reports";

function AdminAnalysisPage() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<ActiveTab>("tests");
  const [tests, setTests] = useState<TestItem[]>([]);
  const [courses, setCourses] = useState<CourseItem[]>([]);
  const [submissions, setSubmissions] = useState<SubmissionItem[]>([]);
  const [users, setUsers] = useState<AssignedUserItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTestId, setSelectedTestId] = useState<number | null>(null);
  const [selectedAiTestId, setSelectedAiTestId] = useState<number | null>(null);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
  const [detailUserSearch, setDetailUserSearch] = useState("");
  const [detailDepartmentFilter, setDetailDepartmentFilter] = useState("");
  const [courseUserSearch, setCourseUserSearch] = useState("");
  const [courseDepartmentFilter, setCourseDepartmentFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalysisData();
  }, []);

  const fetchAnalysisData = async () => {
    try {
      setLoading(true);

      const testsResponse = await fetch("/tests/");
      const coursesResponse = await fetch("/courses/");
      const submissionsResponse = await fetch(
        "/test-submissions/"
      );
      const usersResponse = await fetch("/users/");

      if (!testsResponse.ok) throw new Error("Failed to fetch tests");
      if (!coursesResponse.ok) throw new Error("Failed to fetch courses");
      if (!submissionsResponse.ok) {
        throw new Error("Failed to fetch test submissions");
      }

      const testsData = await testsResponse.json();
      const coursesData = await coursesResponse.json();
      const submissionsData = await submissionsResponse.json();

      let usersData: AssignedUserItem[] = [];
      if (usersResponse.ok) {
        usersData = await usersResponse.json();
      }

      setTests(testsData || []);
      setCourses(coursesData || []);
      setSubmissions(submissionsData || []);
      setUsers(usersData || []);
    } catch (error) {
      console.error("Analysis fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const getAssignedUsers = (assignedUsers: string): AssignedUserItem[] => {
    try {
      const parsed = JSON.parse(assignedUsers);

      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed;
    } catch {
      return [];
    }
  };

  const getCourseAssignedUserNames = (assignedUserText?: string) => {
    if (!assignedUserText) {
      return [];
    }

    return assignedUserText
      .split(",")
      .map((user) => user.trim())
      .filter((user) => user !== "");
  };

  const getCourseDepartments = (departmentText?: string) => {
    if (!departmentText) {
      return [];
    }

    return departmentText
      .split(",")
      .map((department) => department.trim())
      .filter((department) => department !== "");
  };

  const getCourseAssignedUsers = (course: CourseItem) => {
    const departments = getCourseDepartments(course.department);

    if (departments.length === 0) {
      return [];
    }

    return users.filter((user) => {
      if (user.role && user.role !== "employee") {
        return false;
      }

      return departments.includes(user.department || "");
    });
  };

  const getCourseAssignedCount = (course: CourseItem) => {
    const departmentUsers = getCourseAssignedUsers(course);

    if (departmentUsers.length > 0) {
      return departmentUsers.length;
    }

    const assignedUsers = getCourseAssignedUserNames(course.assigned_user);

    if (assignedUsers.length > 0) {
      return assignedUsers.length;
    }

    return Number(course.students || 0);
  };

  const getUserDisplayName = (user: AssignedUserItem) => {
    return user.name || user.username || user.email || `ユーザーID ${user.id}`;
  };

  const filteredTests = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();

    if (!keyword) return tests;

    return tests.filter((test) => {
      return (
        test.title.toLowerCase().includes(keyword) ||
        test.course_name.toLowerCase().includes(keyword) ||
        (test.topic_name || "").toLowerCase().includes(keyword)
      );
    });
  }, [tests, searchTerm]);

  const filteredCourses = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();

    if (!keyword) return courses;

    return courses.filter((course) => {
      return (
        course.name.toLowerCase().includes(keyword) ||
        (course.category || "").toLowerCase().includes(keyword) ||
        (course.instructor || "").toLowerCase().includes(keyword)
      );
    });
  }, [courses, searchTerm]);

  const publishedTests = filteredTests.filter((test) => test.status === "published");
  const selectedTest = tests.find((test) => test.id === selectedTestId) || null;
  const selectedAiTest = tests.find((test) => test.id === selectedAiTestId) || null;
  const selectedCourse =
    courses.find((course) => course.id === selectedCourseId) || null;

  const totalAssignedCount = publishedTests.reduce((total, test) => {
    return total + getAssignedUsers(test.assigned_users).length;
  }, 0);

  const totalTakenCount = submissions.length;
  const totalPassedCount = submissions.filter((item) => item.passed).length;
  const totalFailedCount = submissions.filter((item) => !item.passed).length;

  const totalNotTakenCount = publishedTests.reduce((total, test) => {
    const assignedUsers = getAssignedUsers(test.assigned_users);
    const testSubmissions = submissions.filter((item) => item.test_id === test.id);
    const takenUserIds = testSubmissions.map((item) => item.user_id);

    return (
      total +
      assignedUsers.filter((user) => !takenUserIds.includes(Number(user.id))).length
    );
  }, 0);

  const publishedCourses = filteredCourses.filter(
    (course) => course.status === "published"
  );

  const privateCourses = filteredCourses.filter(
    (course) => course.status !== "published"
  );

  const totalCourseStudents = filteredCourses.reduce((total, course) => {
    return total + getCourseAssignedCount(course);
  }, 0);

  const totalCourseTests = filteredCourses.reduce((total, course) => {
    const relatedTests = tests.filter((test) => test.course_name === course.name);
    return total + relatedTests.length;
  }, 0);

  const averageScore =
    submissions.length > 0
      ? (
          submissions.reduce(
            (total, submission) => total + Number(submission.score),
            0
          ) / submissions.length
        ).toFixed(2)
      : "0";

  const passRate =
    submissions.length > 0
      ? ((totalPassedCount / submissions.length) * 100).toFixed(1)
      : "0";

  const reportSummaryCards = [
    { label: "総コース数", value: courses.length },
    { label: "総テスト数", value: tests.length },
    { label: "総受験数", value: submissions.length },
    { label: "合格者数", value: totalPassedCount },
    { label: "未受験者数", value: totalNotTakenCount },
  ];

  const handleExportCsv = () => {
    alert("CSV出力機能は次のステップで実装します。");
  };

  const handleExportExcel = () => {
    alert("Excel出力機能は次のステップで実装します。");
  };

  const handleDownloadAiReport = (reportTitle: string) => {
    const reportElement = document.getElementById("ai-analysis-report");

    if (!reportElement) {
      return;
    }

    const reportText = reportElement.innerText;
    const blob = new Blob([reportText], {
      type: "text/plain;charset=utf-8",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `${reportTitle}_AI分析レポート.txt`;
    link.click();

    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div style={loadingStyle}>データを読み込み中...</div>;
  }

  return (
    <div style={pageStyle}>
      <div style={headerStyle}>
        <div>
          <h1 style={pageTitleStyle}>
            {activeTab === "tests"
              ? "テスト分析"
              : activeTab === "courses"
              ? "コース分析"
              : "レポート出力"}
          </h1>
          <p style={pageSubtitleStyle}>
            {activeTab === "tests"
              ? "テスト受験状況・合格率・未受験者を確認できます"
              : activeTab === "courses"
              ? "コース公開状況・受講者数・関連テスト数を確認できます"
              : "LMS全体の分析結果をレポート形式で確認・出力できます"}
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate("/admin-dashboard")}
          style={backButtonStyle}
        >
          ← ダッシュボードへ戻る
        </button>
      </div>

      <div style={tabContainerStyle}>
        <button
          type="button"
          onClick={() => {
            setActiveTab("tests");
            setSearchTerm("");
            setSelectedTestId(null);
            setSelectedAiTestId(null);
            setSelectedCourseId(null);
          }}
          style={{
            ...tabButtonStyle,
            ...(activeTab === "tests" ? activeTabButtonStyle : {}),
          }}
        >
          テスト分析
        </button>

        <button
          type="button"
          onClick={() => {
            setActiveTab("courses");
            setSearchTerm("");
            setSelectedTestId(null);
            setSelectedAiTestId(null);
            setSelectedCourseId(null);
          }}
          style={{
            ...tabButtonStyle,
            ...(activeTab === "courses" ? activeTabButtonStyle : {}),
          }}
        >
          コース分析
        </button>

        <button
          type="button"
          onClick={() => {
            setActiveTab("reports");
            setSearchTerm("");
            setSelectedTestId(null);
            setSelectedAiTestId(null);
            setSelectedCourseId(null);
          }}
          style={{
            ...tabButtonStyle,
            ...(activeTab === "reports" ? activeTabButtonStyle : {}),
          }}
        >
          レポート出力
        </button>
      </div>

      {activeTab === "tests" && (
        <>
          <div style={summaryGridStyle}>
            <div style={summaryCardStyle}>
              <div style={summaryLabelStyle}>公開テスト数</div>
              <div style={summaryValueStyle}>{publishedTests.length}</div>
            </div>

            <div style={summaryCardStyle}>
              <div style={summaryLabelStyle}>対象人数</div>
              <div style={summaryValueStyle}>{totalAssignedCount}</div>
            </div>

            <div style={summaryCardStyle}>
              <div style={summaryLabelStyle}>受験済み</div>
              <div style={summaryValueStyle}>{totalTakenCount}</div>
            </div>

            <div style={summaryCardStyle}>
              <div style={summaryLabelStyle}>未受験</div>
              <div style={summaryValueStyle}>{totalNotTakenCount}</div>
            </div>

            <div style={summaryCardStyle}>
              <div style={summaryLabelStyle}>合格者</div>
              <div style={summaryValueStyle}>{totalPassedCount}</div>
            </div>
          </div>

          <div style={tableCardStyle}>
            <div style={searchBoxStyle}>
              <label style={searchLabelStyle}>
                検索（テスト名・コース名・カテゴリー）
              </label>

              <div style={searchRowStyle}>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="テスト名・コース名で検索"
                  style={searchInputStyle}
                />

                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  style={clearButtonStyle}
                >
                  クリア
                </button>
              </div>

              <div style={countTextStyle}>表示件数：{publishedTests.length} 件</div>
            </div>

            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>テスト名</th>
                  <th style={thStyle}>カテゴリー</th>
                  <th style={thStyle}>コース名</th>
                  <th style={thStyle}>対象人数</th>
                  <th style={thStyle}>受験済み</th>
                  <th style={thStyle}>未受験</th>
                  <th style={thStyle}>合格</th>
                  <th style={thStyle}>不合格</th>
                  <th style={thStyle}>平均点</th>
                  <th style={thStyle}>操作</th>
                </tr>
              </thead>

              <tbody>
                {publishedTests.map((test) => {
                  const assignedUsers = getAssignedUsers(test.assigned_users);
                  const testSubmissions = submissions.filter(
                    (submission) => submission.test_id === test.id
                  );
                  const takenUserIds = testSubmissions.map(
                    (submission) => submission.user_id
                  );
                  const notTakenUsers = assignedUsers.filter(
                    (user) => !takenUserIds.includes(Number(user.id))
                  );
                  const passedCount = testSubmissions.filter(
                    (submission) => submission.passed
                  ).length;
                  const failedCount = testSubmissions.filter(
                    (submission) => !submission.passed
                  ).length;
                  const testAverageScore =
                    testSubmissions.length > 0
                      ? (
                          testSubmissions.reduce(
                            (total, submission) => total + Number(submission.score),
                            0
                          ) / testSubmissions.length
                        ).toFixed(2)
                      : "0";

                  return (
                    <tr key={test.id}>
                      <td style={tdStyle}>{test.title}</td>
                      <td style={tdStyle}>{test.topic_name || "-"}</td>
                      <td style={tdStyle}>{test.course_name}</td>
                      <td style={tdStyle}>{assignedUsers.length}名</td>
                      <td style={tdStyle}>{testSubmissions.length}名</td>
                      <td style={tdStyle}>{notTakenUsers.length}名</td>
                      <td style={tdStyle}>{passedCount}名</td>
                      <td style={tdStyle}>{failedCount}名</td>
                      <td style={tdStyle}>{testAverageScore}点</td>
                      <td style={tdStyle}>
                        <div style={actionButtonGroupStyle}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedTestId(test.id);
                              setSelectedAiTestId(null);
                              setSelectedCourseId(null);
                              setDetailUserSearch("");
                              setDetailDepartmentFilter("");
                            }}
                            style={detailButtonStyle}
                          >
                            詳細
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              setSelectedAiTestId(test.id);
                              setSelectedTestId(null);
                              setSelectedCourseId(null);
                            }}
                            style={aiButtonStyle}
                          >
                            AI分析
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {publishedTests.length === 0 && (
              <div style={emptyStyle}>表示できるテストがありません。</div>
            )}
          </div>

          {selectedTest && (
            <div style={detailCardStyle}>
              <div style={detailHeaderStyle}>
                <div>
                  <h2 style={detailTitleStyle}>{selectedTest.title}</h2>
                  <div style={detailSubtitleStyle}>
                    コース：{selectedTest.course_name}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setSelectedTestId(null)}
                  style={closeButtonStyle}
                >
                  閉じる
                </button>
              </div>

              {(() => {
                const assignedUsers = getAssignedUsers(selectedTest.assigned_users);
                const testSubmissions = submissions.filter(
                  (submission) => submission.test_id === selectedTest.id
                );
                const takenUserIds = testSubmissions.map(
                  (submission) => submission.user_id
                );
                const notTakenUsers = assignedUsers.filter(
                  (user) => !takenUserIds.includes(Number(user.id))
                );

                const getUserBySubmission = (submission: SubmissionItem) => {
                  return assignedUsers.find(
                    (user) => Number(user.id) === submission.user_id
                  );
                };

                const detailDepartmentOptions = Array.from(
                  new Set(
                    assignedUsers
                      .map((user) => user.department)
                      .filter((department): department is string =>
                        Boolean(department)
                      )
                  )
                );

                const filteredTestSubmissions = testSubmissions.filter(
                  (submission) => {
                    const user = getUserBySubmission(submission);
                    const userName = getUserDisplayName(user || {});
                    const department = user?.department || "";

                    const matchesName = userName
                      .toLowerCase()
                      .includes(detailUserSearch.trim().toLowerCase());

                    const matchesDepartment =
                      !detailDepartmentFilter ||
                      department === detailDepartmentFilter;

                    return matchesName && matchesDepartment;
                  }
                );

                const filteredNotTakenUsers = notTakenUsers.filter((user) => {
                  const userName = getUserDisplayName(user);
                  const department = user.department || "";

                  const matchesName = userName
                    .toLowerCase()
                    .includes(detailUserSearch.trim().toLowerCase());

                  const matchesDepartment =
                    !detailDepartmentFilter ||
                    department === detailDepartmentFilter;

                  return matchesName && matchesDepartment;
                });

                return (
                  <>
                    <div style={detailSectionStyle}>
                      <h3 style={sectionTitleStyle}>受験済みユーザー</h3>

                      <div style={detailFilterBoxStyle}>
                        <div>
                          <label style={searchLabelStyle}>ユーザー名で検索</label>
                          <input
                            type="text"
                            value={detailUserSearch}
                            onChange={(event) =>
                              setDetailUserSearch(event.target.value)
                            }
                            placeholder="ユーザー名で検索"
                            style={searchInputStyle}
                          />
                        </div>

                        <div>
                          <label style={searchLabelStyle}>部署で絞り込み</label>
                          <select
                            value={detailDepartmentFilter}
                            onChange={(event) =>
                              setDetailDepartmentFilter(event.target.value)
                            }
                            style={departmentSelectStyle}
                          >
                            <option value="">すべての部署</option>
                            {detailDepartmentOptions.map((department) => (
                              <option key={department} value={department}>
                                {department}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {testSubmissions.length === 0 ? (
                        <div style={emptyStyle}>まだ受験者はいません。</div>
                      ) : (
                        <table style={tableStyle}>
                          <thead>
                            <tr>
                              <th style={thStyle}>ユーザー名</th>
                              <th style={thStyle}>部署</th>
                              <th style={thStyle}>得点</th>
                              <th style={thStyle}>結果</th>
                              <th style={thStyle}>提出日時</th>
                            </tr>
                          </thead>

                          <tbody>
                            {filteredTestSubmissions.map((submission) => {
                              const user = getUserBySubmission(submission);

                              return (
                                <tr key={submission.id}>
                                  <td style={tdStyle}>
                                    {getUserDisplayName(user || {})}
                                  </td>
                                  <td style={tdStyle}>{user?.department || "-"}</td>
                                  <td style={tdStyle}>{submission.score}点</td>
                                  <td style={tdStyle}>
                                    <span
                                      style={{
                                        ...statusBadgeStyle,
                                        backgroundColor: submission.passed
                                          ? "#dcfce7"
                                          : "#fee2e2",
                                        color: submission.passed
                                          ? "#166534"
                                          : "#991b1b",
                                      }}
                                    >
                                      {submission.passed ? "合格" : "不合格"}
                                    </span>
                                  </td>
                                  <td style={tdStyle}>{submission.submitted_at}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>

                    <div style={detailSectionStyle}>
                      <h3 style={sectionTitleStyle}>未受験ユーザー</h3>

                      {filteredNotTakenUsers.length === 0 ? (
                        <div style={emptyStyle}>全員受験済みです。</div>
                      ) : (
                        <div style={userGridStyle}>
                          {filteredNotTakenUsers.map((user, index) => (
                            <div key={`${user.id}-${index}`} style={userCardStyle}>
                              <div style={userNameStyle}>
                                {getUserDisplayName(user)}
                              </div>
                              <div style={userEmailStyle}>{user.email || "-"}</div>
                              <div style={userDeptStyle}>{user.department || "-"}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </>
      )}

      {selectedAiTest && (
        <div style={detailCardStyle}>
          <div style={detailHeaderStyle}>
            <div>
              <h2 style={detailTitleStyle}>AI分析レポート：{selectedAiTest.title}</h2>
              <div style={detailSubtitleStyle}>
                コース：{selectedAiTest.course_name}
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                type="button"
                onClick={() => handleDownloadAiReport(selectedAiTest.title)}
                style={aiButtonStyle}
              >
                ダウンロード
              </button>

              <button
                type="button"
                onClick={() => setSelectedAiTestId(null)}
                style={closeButtonStyle}
              >
                閉じる
              </button>
            </div>
          </div>

          {(() => {
            const assignedUsers = getAssignedUsers(selectedAiTest.assigned_users);
            const testSubmissions = submissions.filter(
              (submission) => submission.test_id === selectedAiTest.id
            );

            const takenUserIds = testSubmissions.map(
              (submission) => submission.user_id
            );

            const notTakenUsers = assignedUsers.filter(
              (user) => !takenUserIds.includes(Number(user.id))
            );

            const passedUsers = testSubmissions.filter(
              (submission) => submission.passed
            );

            const failedUsers = testSubmissions.filter(
              (submission) => !submission.passed
            );

            const averageScoreValue =
              testSubmissions.length > 0
                ? (
                    testSubmissions.reduce(
                      (total, submission) => total + Number(submission.score),
                      0
                    ) / testSubmissions.length
                  ).toFixed(2)
                : "0";

            const passRateValue =
              testSubmissions.length > 0
                ? ((passedUsers.length / testSubmissions.length) * 100).toFixed(1)
                : "0";

            const departmentSummary = assignedUsers.reduce(
              (
                summary: Record<
                  string,
                  { assigned: number; taken: number; passed: number }
                >,
                user
              ) => {
                const department = user.department || "未設定";

                if (!summary[department]) {
                  summary[department] = {
                    assigned: 0,
                    taken: 0,
                    passed: 0,
                  };
                }

                summary[department].assigned += 1;

                const userSubmission = testSubmissions.find(
                  (submission) => submission.user_id === Number(user.id)
                );

                if (userSubmission) {
                  summary[department].taken += 1;

                  if (userSubmission.passed) {
                    summary[department].passed += 1;
                  }
                }

                return summary;
              },
              {}
            );

            return (
              <div id="ai-analysis-report" style={aiReportBoxStyle}>
                <h3 style={sectionTitleStyle}>総合分析</h3>

                <p style={aiReportTextStyle}>
                  このテストは、対象者 {assignedUsers.length}名のうち
                  {testSubmissions.length}名が受験し、{passedUsers.length}名が合格、
                  {failedUsers.length}名が不合格でした。未受験者は
                  {notTakenUsers.length}名です。
                </p>

                <div style={aiSummaryGridStyle}>
                  <div style={aiMiniCardStyle}>対象人数：{assignedUsers.length}名</div>
                  <div style={aiMiniCardStyle}>受験済み：{testSubmissions.length}名</div>
                  <div style={aiMiniCardStyle}>未受験：{notTakenUsers.length}名</div>
                  <div style={aiMiniCardStyle}>合格率：{passRateValue}%</div>
                  <div style={aiMiniCardStyle}>平均点：{averageScoreValue}点</div>
                </div>

                <h3 style={sectionTitleStyle}>部署別分析</h3>

                {Object.entries(departmentSummary).map(([department, summary]) => (
                  <div key={department} style={aiDepartmentRowStyle}>
                    <strong>{department}</strong>
                    <span>
                      対象 {summary.assigned}名 / 受験 {summary.taken}名 / 合格{" "}
                      {summary.passed}名
                    </span>
                  </div>
                ))}

                <h3 style={sectionTitleStyle}>受験済みユーザー</h3>

                {testSubmissions.length === 0 ? (
                  <div style={emptyStyle}>まだ受験者はいません。</div>
                ) : (
                  testSubmissions.map((submission) => {
                    const user = assignedUsers.find(
                      (assignedUser) => Number(assignedUser.id) === submission.user_id
                    );

                    return (
                      <div key={submission.id} style={aiUserRowStyle}>
                        <span>{getUserDisplayName(user || {})}</span>
                        <span>{user?.department || "-"}</span>
                        <span>{submission.score}点</span>
                        <span>{submission.passed ? "合格" : "不合格"}</span>
                      </div>
                    );
                  })
                )}

                <h3 style={sectionTitleStyle}>未受験ユーザー</h3>

                {notTakenUsers.length === 0 ? (
                  <div style={emptyStyle}>全員受験済みです。</div>
                ) : (
                  notTakenUsers.map((user, index) => (
                    <div key={`${user.id}-${index}`} style={aiUserRowStyle}>
                      <span>{getUserDisplayName(user)}</span>
                      <span>{user.department || "-"}</span>
                      <span>{user.email || "-"}</span>
                    </div>
                  ))
                )}

                <h3 style={sectionTitleStyle}>AIコメント</h3>

                <p style={aiReportTextStyle}>
                  合格率が {passRateValue}% のため、
                  {Number(passRateValue) >= 80
                    ? "全体的な理解度は高い状態です。"
                    : Number(passRateValue) >= 50
                    ? "一部の受講者に追加フォローが必要です。"
                    : "多くの受講者に再学習や補足説明が必要です。"}
                  未受験者がいる場合は、対象者へ受験リマインドを送ることをおすすめします。
                </p>

                <h3 style={sectionTitleStyle}>問題別分析</h3>

                <div style={emptyStyle}>
                  問題別の簡単・難しい分析は、回答データの保存形式を確認後に追加できます。
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {activeTab === "courses" && (
        <>
          <div style={summaryGridStyle}>
            <div style={summaryCardStyle}>
              <div style={summaryLabelStyle}>総コース数</div>
              <div style={summaryValueStyle}>{filteredCourses.length}</div>
            </div>

            <div style={summaryCardStyle}>
              <div style={summaryLabelStyle}>公開コース数</div>
              <div style={summaryValueStyle}>{publishedCourses.length}</div>
            </div>

            <div style={summaryCardStyle}>
              <div style={summaryLabelStyle}>非公開コース数</div>
              <div style={summaryValueStyle}>{privateCourses.length}</div>
            </div>

            <div style={summaryCardStyle}>
              <div style={summaryLabelStyle}>登録受講者数</div>
              <div style={summaryValueStyle}>{totalCourseStudents}</div>
            </div>

            <div style={summaryCardStyle}>
              <div style={summaryLabelStyle}>関連テスト数</div>
              <div style={summaryValueStyle}>{totalCourseTests}</div>
            </div>
          </div>

          <div style={tableCardStyle}>
            <div style={searchBoxStyle}>
              <label style={searchLabelStyle}>検索（コース名・カテゴリー・講師名）</label>

              <div style={searchRowStyle}>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="コース名・カテゴリー・講師名で検索"
                  style={searchInputStyle}
                />

                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  style={clearButtonStyle}
                >
                  クリア
                </button>
              </div>

              <div style={countTextStyle}>表示件数：{filteredCourses.length} 件</div>
            </div>

            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>カテゴリー</th>
                  <th style={thStyle}>コース名</th>
                  <th style={thStyle}>講師</th>
                  <th style={thStyle}>部署</th>
                  <th style={thStyle}>受講者数</th>
                  <th style={thStyle}>関連テスト数</th>
                  <th style={thStyle}>ステータス</th>
                  <th style={thStyle}>操作</th>
                </tr>
              </thead>

              <tbody>
                {filteredCourses.map((course) => {
                  const relatedTests = tests.filter(
                    (test) => test.course_name === course.name
                  );

                  return (
                    <tr key={course.id}>
                      <td style={tdStyle}>{course.category || "-"}</td>
                      <td style={tdStyle}>{course.name}</td>
                      <td style={tdStyle}>{course.instructor || "-"}</td>
                      <td style={tdStyle}>{course.department || "-"}</td>
                      <td style={tdStyle}>{getCourseAssignedCount(course)}名</td>
                      <td style={tdStyle}>{relatedTests.length}件</td>
                      <td style={tdStyle}>
                        <span
                          style={{
                            ...statusBadgeStyle,
                            backgroundColor:
                              course.status === "published" ? "#dcfce7" : "#fee2e2",
                            color:
                              course.status === "published" ? "#166534" : "#991b1b",
                          }}
                        >
                          {course.status === "published" ? "公開中" : "非公開"}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedCourseId(course.id);
                            setCourseUserSearch("");
                            setCourseDepartmentFilter("");
                          }}
                          style={detailButtonStyle}
                        >
                          詳細
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {filteredCourses.length === 0 && (
              <div style={emptyStyle}>表示できるコースがありません。</div>
            )}
          </div>

          {selectedCourse && (
            <div style={detailCardStyle}>
              <div style={detailHeaderStyle}>
                <div>
                  <h2 style={detailTitleStyle}>{selectedCourse.category || "-"}</h2>
                  <div style={detailSubtitleStyle}>
                    コース：{selectedCourse.name}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setSelectedCourseId(null)}
                  style={closeButtonStyle}
                >
                  閉じる
                </button>
              </div>

              {(() => {
                const assignedCourseUsers = getCourseAssignedUsers(selectedCourse);
                const assignedUserNames = getCourseAssignedUserNames(
                  selectedCourse.assigned_user
                );
                const relatedTests = tests.filter(
                  (test) => test.course_name === selectedCourse.name
                );

                const departmentOptions = Array.from(
                  new Set(
                    assignedCourseUsers
                      .map((user) => user.department)
                      .filter((department): department is string =>
                        Boolean(department)
                      )
                  )
                );

                const filteredCourseUsers = assignedCourseUsers.filter((user) => {
                  const keyword = courseUserSearch.trim().toLowerCase();
                  const userName = getUserDisplayName(user).toLowerCase();
                  const email = (user.email || "").toLowerCase();
                  const department = user.department || "";

                  const matchesKeyword =
                    !keyword || userName.includes(keyword) || email.includes(keyword);

                  const matchesDepartment =
                    !courseDepartmentFilter ||
                    department === courseDepartmentFilter;

                  return matchesKeyword && matchesDepartment;
                });

                return (
                  <>
                    <div style={courseDetailGridStyle}>
                      <div style={courseDetailBoxStyle}>
                        <div style={summaryLabelStyle}>カテゴリー</div>
                        <div style={courseDetailValueStyle}>
                          {selectedCourse.category || "-"}
                        </div>
                      </div>

                      <div style={courseDetailBoxStyle}>
                        <div style={summaryLabelStyle}>コース名</div>
                        <div style={courseDetailValueStyle}>{selectedCourse.name}</div>
                      </div>

                      <div style={courseDetailBoxStyle}>
                        <div style={summaryLabelStyle}>講師</div>
                        <div style={courseDetailValueStyle}>
                          {selectedCourse.instructor || "-"}
                        </div>
                      </div>

                      <div style={courseDetailBoxStyle}>
                        <div style={summaryLabelStyle}>受講者数</div>
                        <div style={courseDetailValueStyle}>
                          {getCourseAssignedCount(selectedCourse)}名
                        </div>
                      </div>
                    </div>

                    <div style={detailSectionStyle}>
                      <h3 style={sectionTitleStyle}>対象部署</h3>
                      <div style={emptyStyle}>{selectedCourse.department || "-"}</div>
                    </div>

                    <div style={detailSectionStyle}>
                      <h3 style={sectionTitleStyle}>割り当てユーザー</h3>

                      {assignedCourseUsers.length === 0 &&
                      assignedUserNames.length === 0 ? (
                        <div style={emptyStyle}>
                          割り当てユーザーは登録されていません。
                        </div>
                      ) : assignedCourseUsers.length > 0 ? (
                        <>
                          <div style={detailFilterBoxStyle}>
                            <div>
                              <label style={searchLabelStyle}>
                                ユーザー名・メールで検索
                              </label>
                              <input
                                type="text"
                                value={courseUserSearch}
                                onChange={(event) =>
                                  setCourseUserSearch(event.target.value)
                                }
                                placeholder="ユーザー名・メールで検索"
                                style={searchInputStyle}
                              />
                            </div>

                            <div>
                              <label style={searchLabelStyle}>部署で絞り込み</label>
                              <select
                                value={courseDepartmentFilter}
                                onChange={(event) =>
                                  setCourseDepartmentFilter(event.target.value)
                                }
                                style={departmentSelectStyle}
                              >
                                <option value="">すべての部署</option>
                                {departmentOptions.map((department) => (
                                  <option key={department} value={department}>
                                    {department}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div style={courseUserTableScrollStyle}>
                            <table style={tableStyle}>
                              <thead>
                                <tr>
                                  <th style={thStyle}>ユーザー名</th>
                                  <th style={thStyle}>メール</th>
                                  <th style={thStyle}>部署</th>
                                </tr>
                              </thead>

                              <tbody>
                                {filteredCourseUsers.map((user, index) => (
                                  <tr key={`${user.id}-${index}`}>
                                    <td style={tdStyle}>{getUserDisplayName(user)}</td>
                                    <td style={tdStyle}>{user.email || "-"}</td>
                                    <td style={tdStyle}>{user.department || "-"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {filteredCourseUsers.length === 0 && (
                            <div style={emptyStyle}>
                              該当するユーザーがいません。
                            </div>
                          )}
                        </>
                      ) : (
                        <div style={userGridStyle}>
                          {assignedUserNames.map((userName, index) => (
                            <div key={`${userName}-${index}`} style={userCardStyle}>
                              <div style={userNameStyle}>{userName}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div style={detailSectionStyle}>
                      <h3 style={sectionTitleStyle}>関連テスト</h3>

                      {relatedTests.length === 0 ? (
                        <div style={emptyStyle}>関連テストはありません。</div>
                      ) : (
                        <table style={tableStyle}>
                          <thead>
                            <tr>
                              <th style={thStyle}>テスト名</th>
                              <th style={thStyle}>カテゴリー</th>
                              <th style={thStyle}>問題数</th>
                              <th style={thStyle}>ステータス</th>
                            </tr>
                          </thead>

                          <tbody>
                            {relatedTests.map((test) => (
                              <tr key={test.id}>
                                <td style={tdStyle}>{test.title}</td>
                                <td style={tdStyle}>{test.topic_name || "-"}</td>
                                <td style={tdStyle}>{test.question_count}問</td>
                                <td style={tdStyle}>
                                  {test.status === "published" ? "公開中" : "下書き"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </>
      )}

      {activeTab === "reports" && (
        <>
          <div style={summaryGridStyle}>
            {reportSummaryCards.map((card) => (
              <div key={card.label} style={summaryCardStyle}>
                <div style={summaryLabelStyle}>{card.label}</div>
                <div style={summaryValueStyle}>{card.value}</div>
              </div>
            ))}
          </div>

          <div style={reportActionCardStyle}>
            <div>
              <h2 style={reportActionTitleStyle}>レポート出力</h2>
              <p style={pageSubtitleStyle}>
                テスト分析・コース分析の結果をCSVまたはExcel形式で出力できます。
              </p>
            </div>

            <div style={reportButtonGroupStyle}>
              <button type="button" onClick={handleExportCsv} style={csvButtonStyle}>
                CSV出力
              </button>

              <button
                type="button"
                onClick={handleExportExcel}
                style={excelButtonStyle}
              >
                Excel出力
              </button>
            </div>
          </div>

          <div style={tableCardStyle}>
            <h2 style={reportSectionTitleStyle}>テストレポート概要</h2>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>テスト名</th>
                  <th style={thStyle}>コース名</th>
                  <th style={thStyle}>対象人数</th>
                  <th style={thStyle}>受験済み</th>
                  <th style={thStyle}>未受験</th>
                  <th style={thStyle}>合格</th>
                  <th style={thStyle}>不合格</th>
                  <th style={thStyle}>平均点</th>
                </tr>
              </thead>

              <tbody>
                {tests.map((test) => {
                  const assignedUsers = getAssignedUsers(test.assigned_users);
                  const testSubmissions = submissions.filter(
                    (submission) => submission.test_id === test.id
                  );
                  const takenUserIds = testSubmissions.map(
                    (submission) => submission.user_id
                  );
                  const notTakenUsers = assignedUsers.filter(
                    (user) => !takenUserIds.includes(Number(user.id))
                  );
                  const passedCount = testSubmissions.filter(
                    (submission) => submission.passed
                  ).length;
                  const failedCount = testSubmissions.filter(
                    (submission) => !submission.passed
                  ).length;
                  const testAverageScore =
                    testSubmissions.length > 0
                      ? (
                          testSubmissions.reduce(
                            (total, submission) => total + Number(submission.score),
                            0
                          ) / testSubmissions.length
                        ).toFixed(2)
                      : "0";

                  return (
                    <tr key={test.id}>
                      <td style={tdStyle}>{test.title}</td>
                      <td style={tdStyle}>{test.course_name}</td>
                      <td style={tdStyle}>{assignedUsers.length}名</td>
                      <td style={tdStyle}>{testSubmissions.length}名</td>
                      <td style={tdStyle}>{notTakenUsers.length}名</td>
                      <td style={tdStyle}>{passedCount}名</td>
                      <td style={tdStyle}>{failedCount}名</td>
                      <td style={tdStyle}>{testAverageScore}点</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={tableCardStyle}>
            <h2 style={reportSectionTitleStyle}>コースレポート概要</h2>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>コース名</th>
                  <th style={thStyle}>カテゴリー</th>
                  <th style={thStyle}>講師</th>
                  <th style={thStyle}>部署</th>
                  <th style={thStyle}>受講者数</th>
                  <th style={thStyle}>関連テスト数</th>
                  <th style={thStyle}>ステータス</th>
                </tr>
              </thead>

              <tbody>
                {courses.map((course) => {
                  const relatedTests = tests.filter(
                    (test) => test.course_name === course.name
                  );

                  return (
                    <tr key={course.id}>
                      <td style={tdStyle}>{course.name}</td>
                      <td style={tdStyle}>{course.category || "-"}</td>
                      <td style={tdStyle}>{course.instructor || "-"}</td>
                      <td style={tdStyle}>{course.department || "-"}</td>
                      <td style={tdStyle}>{getCourseAssignedCount(course)}名</td>
                      <td style={tdStyle}>{relatedTests.length}件</td>
                      <td style={tdStyle}>
                        <span
                          style={{
                            ...statusBadgeStyle,
                            backgroundColor:
                              course.status === "published" ? "#dcfce7" : "#fee2e2",
                            color:
                              course.status === "published" ? "#166534" : "#991b1b",
                          }}
                        >
                          {course.status === "published" ? "公開中" : "非公開"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={reportActionCardStyle}>
            <div>
              <h2 style={reportActionTitleStyle}>総合サマリー</h2>
              <p style={pageSubtitleStyle}>
                平均点：{averageScore}点 / 合格率：{passRate}% / 不合格者数：
                {totalFailedCount}名
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  padding: "32px",
  backgroundColor: "#f3f4f6",
  minHeight: "100vh",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "22px",
};

const pageTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "32px",
  fontWeight: 900,
  color: "#0f172a",
};

const pageSubtitleStyle: React.CSSProperties = {
  margin: "8px 0 0",
  color: "#64748b",
  fontSize: "16px",
};

const tabContainerStyle: React.CSSProperties = {
  display: "flex",
  gap: "14px",
  marginBottom: "24px",
};

const tabButtonStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  backgroundColor: "#ffffff",
  color: "#334155",
  padding: "14px 24px",
  borderRadius: "14px",
  fontWeight: 900,
  fontSize: "15px",
  cursor: "pointer",
};

const activeTabButtonStyle: React.CSSProperties = {
  backgroundColor: "#0f766e",
  color: "#ffffff",
  border: "none",
  boxShadow: "0 10px 24px rgba(15, 118, 110, 0.22)",
};

const backButtonStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  backgroundColor: "#ffffff",
  color: "#0f172a",
  padding: "14px 24px",
  borderRadius: "14px",
  fontWeight: 800,
  cursor: "pointer",
};

const loadingStyle: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "22px",
  fontWeight: 800,
};

const summaryGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
  gap: "18px",
  marginBottom: "24px",
};

const summaryCardStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  borderRadius: "20px",
  padding: "22px",
  border: "1px solid #e2e8f0",
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
};

const summaryLabelStyle: React.CSSProperties = {
  color: "#64748b",
  fontSize: "14px",
  fontWeight: 800,
};

const summaryValueStyle: React.CSSProperties = {
  marginTop: "10px",
  color: "#0f172a",
  fontSize: "30px",
  fontWeight: 900,
};

const tableCardStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  borderRadius: "24px",
  padding: "28px",
  border: "1px solid #e2e8f0",
  boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
  marginBottom: "24px",
  overflowX: "auto",
};

const searchBoxStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  borderRadius: "18px",
  padding: "20px",
  marginBottom: "26px",
};

const searchLabelStyle: React.CSSProperties = {
  display: "block",
  fontWeight: 900,
  marginBottom: "10px",
};

const searchRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "12px",
};

const searchInputStyle: React.CSSProperties = {
  flex: 1,
  border: "1px solid #cbd5e1",
  borderRadius: "12px",
  padding: "14px 16px",
  fontSize: "15px",
};

const clearButtonStyle: React.CSSProperties = {
  width: "90px",
  border: "1px solid #cbd5e1",
  backgroundColor: "#ffffff",
  borderRadius: "12px",
  fontWeight: 900,
  cursor: "pointer",
};

const countTextStyle: React.CSSProperties = {
  marginTop: "18px",
  fontWeight: 800,
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
};

const thStyle: React.CSSProperties = {
  padding: "16px",
  textAlign: "left",
  borderBottom: "1px solid #e2e8f0",
  color: "#0f172a",
  fontWeight: 900,
  whiteSpace: "nowrap",
};

const tdStyle: React.CSSProperties = {
  padding: "16px",
  borderBottom: "1px solid #e2e8f0",
  color: "#0f172a",
  fontWeight: 600,
  whiteSpace: "nowrap",
};

const detailButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "#eff6ff",
  color: "#2563eb",
  padding: "10px 16px",
  borderRadius: "12px",
  fontWeight: 900,
  cursor: "pointer",
};

const emptyStyle: React.CSSProperties = {
  padding: "22px",
  backgroundColor: "#f8fafc",
  borderRadius: "14px",
  color: "#64748b",
  fontWeight: 700,
};

const detailCardStyle: React.CSSProperties = {
  marginTop: "24px",
  backgroundColor: "#ffffff",
  borderRadius: "24px",
  padding: "28px",
  border: "1px solid #e2e8f0",
};

const detailHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "24px",
};

const detailTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "24px",
  fontWeight: 900,
};

const detailSubtitleStyle: React.CSSProperties = {
  marginTop: "8px",
  color: "#64748b",
};

const closeButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "#334155",
  color: "#ffffff",
  padding: "12px 18px",
  borderRadius: "12px",
  fontWeight: 900,
  cursor: "pointer",
};

const detailSectionStyle: React.CSSProperties = {
  marginTop: "26px",
};

const sectionTitleStyle: React.CSSProperties = {
  marginBottom: "16px",
  color: "#0f172a",
};

const statusBadgeStyle: React.CSSProperties = {
  padding: "7px 12px",
  borderRadius: "999px",
  fontSize: "13px",
  fontWeight: 900,
};

const userGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "14px",
};

const userCardStyle: React.CSSProperties = {
  border: "1px solid #e2e8f0",
  backgroundColor: "#f8fafc",
  borderRadius: "14px",
  padding: "16px",
};

const userNameStyle: React.CSSProperties = {
  fontWeight: 900,
  color: "#0f172a",
};

const userEmailStyle: React.CSSProperties = {
  marginTop: "6px",
  color: "#64748b",
  fontSize: "14px",
};

const userDeptStyle: React.CSSProperties = {
  marginTop: "6px",
  color: "#0f766e",
  fontSize: "13px",
  fontWeight: 800,
};

const detailFilterBoxStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 260px",
  gap: "16px",
  backgroundColor: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: "16px",
  padding: "18px",
  marginBottom: "24px",
};

const departmentSelectStyle: React.CSSProperties = {
  width: "100%",
  border: "1px solid #cbd5e1",
  borderRadius: "12px",
  padding: "14px 16px",
  fontSize: "15px",
  backgroundColor: "#ffffff",
};

const courseUserTableScrollStyle: React.CSSProperties = {
  maxHeight: "330px",
  overflowY: "auto",
  border: "1px solid #e2e8f0",
  borderRadius: "14px",
};

const reportActionCardStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  borderRadius: "24px",
  padding: "28px",
  border: "1px solid #e2e8f0",
  boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
  marginBottom: "24px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const reportActionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "22px",
  fontWeight: 900,
  color: "#0f172a",
};

const reportButtonGroupStyle: React.CSSProperties = {
  display: "flex",
  gap: "12px",
};

const csvButtonStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  backgroundColor: "#ffffff",
  color: "#0f172a",
  padding: "13px 22px",
  borderRadius: "14px",
  fontWeight: 900,
  cursor: "pointer",
};

const excelButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "#0f766e",
  color: "#ffffff",
  padding: "13px 22px",
  borderRadius: "14px",
  fontWeight: 900,
  cursor: "pointer",
};

const reportSectionTitleStyle: React.CSSProperties = {
  margin: "0 0 20px",
  color: "#0f172a",
  fontSize: "22px",
  fontWeight: 900,
};

const actionButtonGroupStyle: React.CSSProperties = {
  display: "flex",
  gap: "8px",
};

const aiButtonStyle: React.CSSProperties = {
  border: "none",
  backgroundColor: "#0f766e",
  color: "#ffffff",
  padding: "10px 16px",
  borderRadius: "12px",
  fontWeight: 900,
  cursor: "pointer",
};

const aiReportBoxStyle: React.CSSProperties = {
  backgroundColor: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: "18px",
  padding: "22px",
};

const aiReportTextStyle: React.CSSProperties = {
  color: "#334155",
  fontSize: "15px",
  lineHeight: 1.8,
  fontWeight: 700,
};

const aiSummaryGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
  gap: "12px",
  margin: "18px 0 26px",
};

const aiMiniCardStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "14px",
  padding: "14px",
  fontWeight: 900,
  color: "#0f172a",
};

const aiDepartmentRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  padding: "14px",
  marginBottom: "10px",
};

const aiUserRowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "12px",
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  padding: "14px",
  marginBottom: "10px",
  fontWeight: 700,
};

const courseDetailGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "14px",
};

const courseDetailBoxStyle: React.CSSProperties = {
  backgroundColor: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: "14px",
  padding: "16px",
};

const courseDetailValueStyle: React.CSSProperties = {
  marginTop: "8px",
  color: "#0f172a",
  fontSize: "18px",
  fontWeight: 900,
};

export default AdminAnalysisPage;